import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaCar, FaGasPump, FaTachometerAlt } from 'react-icons/fa';
import { TbSteeringWheel } from 'react-icons/tb';
import Skeleton from '../components/ui/Skeleton';
import Divider from '../components/ui/Divider';

function CarMake() {
  const { slug } = useParams();
  const [listing, setListing] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchListing = async () => {
      try {
        const response = await fetch('/api/listing');
        let data = await response.json();
        data.sort((a, b) => new Date(b.date) - new Date(a.date));
        data = data.filter(item => item.visibility === "Active");
        if (slug) {
          data = data.filter(item => item.make === slug);
        }
        setListing(data);
      } catch (error) {
        // Error handled silently
      } finally {
        setLoading(false);
      }
    };
    fetchListing();
  }, [slug]);

  const renderSkeleton = () => (
    <div className="relative shadow-md rounded-lg overflow-hidden p-4">
      <Skeleton className="w-full h-48 mb-2 rounded-md" />
      <Skeleton className="h-5 w-3/4 mb-2" />
      <Skeleton className="h-5 w-1/2 mb-2" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-gray-50 py-12 md:py-20 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
            Cars by Make: <span className="text-[#DC2626]">{slug}</span>
          </h1>
          <p className="text-lg text-gray-600">
            Browse all {slug} vehicles in our inventory
          </p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {Array(8).fill(null).map((_, index) => (
              <div key={index}>{renderSkeleton()}</div>
            ))}
          </div>
        ) : listing.length === 0 ? (
          <div className="text-center mt-20 mb-60">
            <h2 className="text-xl font-bold">No Cars Found</h2>
            <p className="text-gray-500">No {slug} vehicles available at the moment.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {listing.map((item) => (
              <div key={item._id} className="relative bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
                <div className="relative">
                  <div className="overflow-hidden bg-gray-100">
                    <Link to={`/cars/${item._id}`}>
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-full h-56 object-cover transition-transform duration-500 ease-in-out hover:scale-110"
                      />
                    </Link>
                    {item.itemCondition === "Used" && (
                      <span className='absolute top-3 left-3 bg-[#DC2626] text-white px-3 py-1.5 rounded-full text-xs font-semibold shadow-lg uppercase tracking-wide'>
                        {item.itemCondition}
                      </span>
                    )}
                    <div className="absolute top-3 right-3 bg-[#DC2626] px-4 py-2 rounded-full shadow-lg">
                      <p className='text-base font-bold text-white whitespace-nowrap drop-shadow-sm'>${item.price}</p>
                    </div>
                  </div>
                  
                  <div className="p-5">
                    <Link to={`/cars/${item._id}`}>
                      <h2 className="font-display text-lg font-semibold text-gray-900 mb-4 hover:text-[#DC2626] transition-colors line-clamp-2 min-h-[3.5rem]">
                        {item.title}
                      </h2>
                    </Link>
                    
                    <Divider className="my-3" />
                    
                    <div className='grid grid-cols-2 gap-3 pt-2'>
                      <div className='flex items-center gap-2'>
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-50 flex items-center justify-center">
                          <FaGasPump className="text-[#DC2626] text-sm" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Fuel</p>
                          <p className="text-sm text-gray-900 font-semibold truncate">{item.fuelType}</p>
                        </div>
                      </div>
                      
                      <div className='flex items-center gap-2'>
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-50 flex items-center justify-center">
                          <FaCar className="text-[#DC2626] text-sm" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Body</p>
                          <p className="text-sm text-gray-900 font-semibold truncate">{item.bodyType}</p>
                        </div>
                      </div>
                      
                      <div className='flex items-center gap-2'>
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-50 flex items-center justify-center">
                          <FaTachometerAlt className="text-[#DC2626] text-sm" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Mileage</p>
                          <p className="text-sm text-gray-900 font-semibold truncate">{item.mileage} {item.mileageUnit}</p>
                        </div>
                      </div>
                      
                      <div className='flex items-center gap-2'>
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-50 flex items-center justify-center">
                          <TbSteeringWheel className="text-[#DC2626] text-base" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Transmission</p>
                          <p className="text-sm text-gray-900 font-semibold truncate">{item.vehicleTransmission}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default CarMake;
